/**
 * protocol.ts — the client/server contract: room identity, reliable message
 * names and their payload types.
 *
 * Colyseus carries these as ordinary reliable messages; Colyseus Schema state
 * sync is NOT used (AGENTS.md stack). High-frequency world state travels as
 * snapshots through the codec instead — reliable events live here.
 */
import { FEEL } from './feel.js';
import { sanitizeLoadout } from './armor.js';
import { sanitizeChatText } from './chat.js';
import { toSingleLine } from './text.js';
/** The single Colyseus room type. Rooms are told apart by their `code`. */
export const ROOM_NAME = 'duel';
/** Hard cap on players in one room. */
export const MAX_PLAYERS = 8;
/** Message names. Snapshots are frequent and lossy-tolerant; the rest are events. */
export const MSG = {
    /** Server → joining client, once: who you are and who is already here. */
    Welcome: 'welcome',
    /** Server → everyone else: someone joined. */
    PlayerJoined: 'playerJoined',
    /** Server → everyone: someone left. */
    PlayerLeft: 'playerLeft',
    /** Client → server: the player's intent for this frame. */
    Input: 'in',
    /** Server → everyone: an encoded world snapshot, every tick. */
    Snapshot: 'snap',
    /** Server → everyone: what happened in combat this tick (hits, blocks, kills). */
    Combat: 'cmb',
    /** Client → server: change a feel constant (debug tuning panel). */
    Tune: 'tune',
    /** Server → everyone: a feel constant changed; mirror it locally. */
    Tuned: 'tuned',
    /** Client → server: wear this loadout (the wardrobe, I). */
    SetLoadout: 'kit',
    /**
     * Server → everyone: the round phase or the scores changed (`RoundMsg` from
     * `round.ts`). Sent on every change and once to each joiner, never per tick.
     */
    Round: 'round',
    /** Client → server: say something in room chat. */
    Say: 'say',
    /** Server → everyone: someone said something. */
    Chat: 'chat',
};
/** Coerces an untrusted equip message. Anything unrecognized comes back bare. */
export function sanitizeSetLoadout(raw) {
    return sanitizeLoadout(raw?.kit);
}
/** Coerces an untrusted say message. Undefined means there was nothing to send. */
export function sanitizeSay(raw) {
    return sanitizeChatText(raw?.text);
}
/**
 * Discrete actions a client can ask for. One per command at most; the server
 * decides whether it is legal right now (AGENTS.md: server-authoritative).
 */
export const ACTION = {
    None: 0,
    /** LMB: slash from wherever the dial points. The angle rides along on the command. */
    Slash: 1,
    /** Scroll forward. */
    Stab: 2,
    /** Q: cancels a windup before the active window starts. */
    Feint: 3,
    /** A flourish of the sword. Cosmetic; cannot hit anything. */
    Flourish: 4,
    /** A bow. Cosmetic; cannot hit anything. */
    Bow: 5,
};
/** The emote an action asks for, or undefined if it is not an emote. */
export function actionEmote(action) {
    if (action === ACTION.Flourish)
        return 'flourish';
    if (action === ACTION.Bow)
        return 'bow';
    return undefined;
}
/** The attack clip an action selects, or undefined if it is not an attack. */
export function actionClip(action) {
    switch (action) {
        case ACTION.Slash:
            return 'slash';
        case ACTION.Stab:
            return 'stab';
        default:
            return undefined;
    }
}
const clampAxis = (value) => {
    const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    return n < -1 ? -1 : n > 1 ? 1 : n;
};
/**
 * Wraps an angle into (-PI, PI]. Exactly half a turn maps to +PI, not -PI, so a
 * 180-degree turn resolves consistently counter-clockwise instead of flipping
 * sign on rounding.
 */
export function wrapAngle(radians) {
    const twoPi = 2 * Math.PI;
    const shifted = ((radians + Math.PI) % twoPi + twoPi) % twoPi;
    return shifted === 0 ? Math.PI : shifted - Math.PI;
}
const isAction = (value) => typeof value === 'number' && Object.values(ACTION).includes(value);
/** Coerces an untrusted input message into a safe command. Server-side gate. */
export function sanitizeInput(raw) {
    if (typeof raw !== 'object' || raw === null)
        return undefined;
    const { seq, f, r, yaw, pitch, a, ang, b } = raw;
    if (typeof seq !== 'number' || !Number.isFinite(seq) || seq < 0)
        return undefined;
    if (typeof yaw !== 'number' || !Number.isFinite(yaw))
        return undefined;
    const safePitch = typeof pitch === 'number' && Number.isFinite(pitch) ? pitch : 0;
    return {
        seq: Math.floor(seq),
        f: clampAxis(f),
        r: clampAxis(r),
        yaw: wrapAngle(yaw),
        // Pitch is clamped to the same range the client's camera allows, so a
        // hand-rolled packet cannot aim a guard somewhere unreachable.
        pitch: Math.min(FEEL.PITCH_MAX, Math.max(FEEL.PITCH_MIN, safePitch)),
        a: isAction(a) ? a : ACTION.None,
        ang: typeof ang === 'number' && Number.isFinite(ang) ? wrapAngle(ang) : 0,
        b: b === true,
        c: raw.c === true,
    };
}
/** Reasons a join can be refused, sent as the error message text. */
export const JoinError = {
    BadCode: 'BAD_CODE',
    RoomFull: 'ROOM_FULL',
};
// ---- room codes ----
export const ROOM_CODE_MIN = 3;
export const ROOM_CODE_MAX = 12;
const ROOM_CODE_RE = /^[a-z0-9]+$/;
/** Codes are case-insensitive; both sides normalize before comparing. */
export function normalizeRoomCode(raw) {
    return raw.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}
export function isValidRoomCode(code) {
    return (code.length >= ROOM_CODE_MIN && code.length <= ROOM_CODE_MAX && ROOM_CODE_RE.test(code));
}
// ---- display names ----
export const NAME_MAX = 16;
export const DEFAULT_NAME = 'Duelist';
/**
 * Names are untrusted input rendered into the DOM, so they are reduced to a
 * short single-line string here and validated on the server, never the client.
 */
export function sanitizeDisplayName(raw) {
    return toSingleLine(raw, NAME_MAX) ?? DEFAULT_NAME;
}
//# sourceMappingURL=protocol.js.map